import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import styled from 'styled-components';
import translator from 'common/i18n';
import { MQUERY } from 'common/constants';
import ChevronLeftIcon from '../../../i/svg-files/icons/chevron-left.svg';
import ChevronRightIcon from '../../../i/svg-files/icons/chevron-right.svg';

const PX_TO_REM = 10;
const SPACE_BETWEEN = 24;

export default function PaginationControls( { listRef, slides, numEntries, pageSize, hasAdder, modifierClass='controls' }) {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(1);
    const [maxSlides, setMaxSlides] = useState(null);
    const [singleSlide, setSingleSlide] = useState(null);
    const [scrolled, setScrolled] = useState(0);
    // slides.current if it is from a component ref, slides if it's from a widget 
    const allSlides = (slides && slides?.current) ? slides.current.map(item => item.current) : slides;
    // listRef.current if it is from a component ref, lisRef if it's from a widget 
    listRef = listRef?.current ? listRef.current : listRef;

    useEffect(() => {

        if(numEntries) {
            if(hasAdder && numEntries <= 4) {
                // Dynamic Video Rail exception
                setMaxSlides(Math.ceil(numEntries / pageSize) + 1);
            } else {
                setMaxSlides(Math.ceil(numEntries / pageSize));
            }
        }

        return() => {
            setCurrentSlideIndex(1);
            
            if(listRef) {
               return listRef.style.transform = `translateX(0rem)`;
            }
        }
    }, []);

    // we need this check due to accessibility change
    // when toggling it sets allSlides to null, then back again to the element
    // but handleNavigation does not catch this change unless we cause a re-rendering
    useEffect(() => {
        
        setSingleSlide(allSlides[0]);
    },[allSlides[0]]);

    useEffect(() => {
        // This check needs to be done here due to asynchronous behavior,
        // and not within the "prev" direction check.
        if (currentSlideIndex === 1 ) { 
            setScrolled(0);
        }
    },[currentSlideIndex]);

    const handleNavigation = direction => {
        const oddNum = numEntries%pageSize;
        const beforeLast = (maxSlides - currentSlideIndex) === 1;
        const isLast = currentSlideIndex === maxSlides;
        const oddDistance = (singleSlide.getBoundingClientRect().width + SPACE_BETWEEN) * oddNum;
        const listWidth = listRef.getBoundingClientRect().width;
      
        if (direction === 'next') {

            const distance = (beforeLast && oddNum) ? oddDistance : listWidth;

            if (currentSlideIndex === maxSlides) {

                listRef.style.transform = `translateX(0rem)`;

                return setCurrentSlideIndex(1);
            }

            setCurrentSlideIndex(prev => prev + 1);
            setScrolled(prev => prev + distance);
            listRef.style.transform = `translateX(${-(distance + scrolled) / PX_TO_REM}rem)`;
        }

        if (direction === 'prev') {

            const distance = (isLast && oddNum) ? oddDistance : listWidth;

            if (currentSlideIndex === 1) {

                listRef.style.transform = `translateX(0rem)`;

                return setCurrentSlideIndex(1);
            }

            setCurrentSlideIndex(prev => prev - 1);
            setScrolled(prev => prev - distance);            
            listRef.style.transform = `translateX(${-(scrolled - distance) / PX_TO_REM}rem)`;
        }
    }

    if(numEntries <= pageSize && !hasAdder) return '';
    if(numEntries < pageSize && hasAdder) return '';

    window.addEventListener('resize', () => {
        window.requestAnimationFrame(() => {
            listRef.style.transform = `translateX(0rem)`;

            return setCurrentSlideIndex(1);
        });
    });

    return (
        <Controls className={modifierClass}>
            <Arrow $isDisabled={currentSlideIndex <= 1} onClick={() => handleNavigation('prev')}>
                <ChevronLeftIcon />
                <ArrowLabel>{translator.lookup('label.previous')}</ArrowLabel>
            </Arrow>
            <Arrow onClick={() => handleNavigation('next')}>
                <ChevronRightIcon />
                <ArrowLabel>{translator.lookup('label.next')}</ArrowLabel>
            </Arrow>
        </Controls>
    );
}

export const Arrow = styled.button`
    background-color: ${({ $isDisabled }) => ($isDisabled ? 'var(--buttonDisabled)' : 'var(--button)')};
    pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
    display:flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.25s ease;
    position: relative;
    cursor: pointer;
    display: grid;
    height: 4rem;
    width: 4rem;

    &:first-of-type {
        border-top-left-radius: .4rem;
        border-bottom-left-radius: .4rem;
    }

    &:last-of-type {
        margin-left: .1rem;
        border-top-right-radius: .4rem;
        border-bottom-right-radius: .4rem;
    }

    &:hover {
        background-color: var(--buttonHovered); 
    }

    svg {
        height: 2.4rem;
        fill: var(--primary);
    }
`;

export const ArrowLabel = styled.span`
    text-indent: -9999px;
    position: absolute;
`;

export const Controls = styled.div`
    display: none;

    // this breakpoint for controls that not rely on
    // hover to display
    @media ${MQUERY.TABLET} {
        &.small {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            opacity: 1;
            position: absolute;
            right: var(--widget-padding-desktop);
            top: .5rem;
            transform: none;

            ${Arrow} {
                border-radius: .2rem;
                height: 2.4rem;
                width: 2.4rem;

                &:last-of-type {
                    margin-left: .4rem;
                }

                svg {
                    height: 1.8rem;
                    width: 1.8rem;
                }
            }
        }
    }

    // do not change this breakpoint, 
    // otherwise we will have an unfunctional rail on iPad Pro (1024px)
    @media ${ MQUERY.DESKTOP} {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        position: absolute;
        right: -1rem;
        top: 50%;
        transform: translateY(-50%);
        transition: opacity 0.25s ease;
        opacity: 0;
        // z-index higher than the card
        z-index: 22;

        &.related-articles {
            right: -2rem;
        }

        &.shoppable-listing {
            right: 1rem;
        }
    }      

    @media ${ MQUERY.DESKTOP_FULLSCREEN } {
        right: -5.5rem;

        &.related-articles {
            right: -2.5rem;
        }
    }
`;